import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Waypoint } from 'react-waypoint';
import { useSingleEmailComposer, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { formatFileSize } from '../../../../../../utils/fileSize';
import { createRawRichTextContentStateWithHtmlStringValue } from '../../../../../../extViewmodels/sdk/Utils';
import { EmailMessageViewModel } from '../../../../../../viewmodels/AppViewModels';
import { FileRequestLinkPlaceholder } from '../../../../../../models/Token';
import { useContact, useContactFilesQuery } from '../../../../../../queries';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../../components/DataGrid';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { Placeholder } from '../../../../../components/Placeholder';
import { FileTextIcon } from '../../../../../components/svgs/icons/FileTextIcon';
import { Button } from '../../../../../components-v2/Button';
import { Pill } from '../../../../../components-v2/Pill';
import { styleSheet } from './styles';

const SOURCE_TYPE_LABELS: Record<Api.SharedFileSourceType, string> = {
	[Api.SharedFileSourceType.Unknown]: 'Unknown',
	[Api.SharedFileSourceType.Note]: 'Note',
	[Api.SharedFileSourceType.Survey]: 'Survey',
	[Api.SharedFileSourceType.Automation]: 'Automation',
	[Api.SharedFileSourceType.SingleEmail]: 'Single Email',
	[Api.SharedFileSourceType.BulkEmailMessage]: 'Bulk Email',
	[Api.SharedFileSourceType.HtmlNewsletter]: 'Newsletter',
	[Api.SharedFileSourceType.SecureSend]: 'Secure Send',
	[Api.SharedFileSourceType.SecureRequest]: 'Secure Request',
};

function humanizeSourceType(sourceType: Api.SharedFileSourceType): string {
	return SOURCE_TYPE_LABELS[sourceType] ?? sourceType;
}

function formatDate(dateStr?: string): string {
	if (!dateStr) {
		return '';
	}
	return new Date(dateStr).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function getUploaderName(
	direction: Api.SharedFileDirection,
	creator?: Api.UserReference,
	contact?: Api.IContact
): string {
	if (direction === Api.SharedFileDirection.Received) {
		return [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');
	}
	if (!creator) {
		return '';
	}
	return creator.fullName ?? [creator.firstName, creator.lastName].filter(Boolean).join(' ');
}

type IProps = {
	contactId?: string;
};

export const ContactFiles = ({ contactId }: IProps) => {
	const userSession = useUserSession();
	const singleEmailComposer = useSingleEmailComposer();
	const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

	const handleRequestFiles = () => {
		const contactVm = new Api.ContactViewModel(userSession, { id: contactId } as Api.IContact);
		const emailMessage = new EmailMessageViewModel<File>(userSession, true);
		emailMessage.contactsToAdd.add(contactVm);
		emailMessage.options.sendSingleEmail = true;
		emailMessage.content = createRawRichTextContentStateWithHtmlStringValue(
			`<p>${FileRequestLinkPlaceholder.symbol}</p>`
		);
		singleEmailComposer.showWithEmailMessage(emailMessage, [contactVm]);
	};

	const contactQuery = useContact({ contactId: contactId ?? '', enabled: !!contactId });

	const filesQuery = useContactFilesQuery({
		contactId: contactId ?? '',
		enabled: !!contactId,
	});

	const { fetchNextPage, hasNextPage, isFetchingNextPage } = filesQuery;

	const files = useMemo(
		() => filesQuery.data?.pages.flatMap((page: Api.IPagedCollection<Api.ISharedFile>) => page.values) ?? [],
		[filesQuery.data]
	);

	const handleDownload = async (file: Api.ISharedFile) => {
		setDownloadingFileId(file.id);
		try {
			const attachment = await userSession.webServiceHelper.callAsync<Api.IFileAttachment>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `attachment/${file.fileAttachment.id}` }),
				'GET'
			);
			if (attachment.url) {
				const link = document.createElement('a');
				link.href = attachment.url;
				link.download = attachment.fileName;
				link.click();
				link.remove();
			}
		} catch {
			toast.error('Failed to download file');
		} finally {
			setDownloadingFileId(null);
		}
	};

	if (!contactId) {
		return;
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.actionsContainer)}>
					{/** Upload and Send File buttons pending their own API support */}
					{/*<Button>
						<PlusIcon width={12} fillColor='white' /> Upload
					</Button>
					<Button variant='secondary'>
						<PaperPlaneIcon />
						Send File
					</Button>*/}
					<Button variant='secondary' onClick={handleRequestFiles}>
						Request Files
					</Button>
				</div>
			</div>

			<div className={css(styleSheet.content)}>
				<Placeholder
					isLoading={filesQuery.isPending}
					isEmpty={!filesQuery.isPending && files.length === 0}
					message='No files have been shared with this contact yet.'
				/>
				{!filesQuery.isPending && files.length > 0 && (
					<DataGrid styles={[styleSheet.dataGrid]}>
						<DataGridColumns>
							<DataGridColumn>Filename</DataGridColumn>
							<DataGridColumn>Date</DataGridColumn>
							<DataGridColumn>Source</DataGridColumn>
							<DataGridColumn>Uploader</DataGridColumn>
							<DataGridColumn>Size</DataGridColumn>
						</DataGridColumns>
						<DataGridRows>
							{files.map((file: Api.ISharedFile) => {
								const isDownloading = downloadingFileId === file.id;
								return (
									<DataGridRow key={file.id}>
										<DataGridRowItem>
											<div className={css(styleSheet.filenameCell)}>
												<FileTextIcon width={16} height={16} />
												<Button
													variant='link'
													disabled={isDownloading}
													onClick={() => handleDownload(file)}
													title='Download'
												>
													{file.fileAttachment.fileName}
												</Button>
											</div>
										</DataGridRowItem>
										<DataGridRowItem>{formatDate(file.creationDate)}</DataGridRowItem>
										<DataGridRowItem>
											<Pill
												variant='label'
												color='gray'
												label={humanizeSourceType(file.source.sourceType)}
												size='small'
											/>
										</DataGridRowItem>
										<DataGridRowItem>
											{getUploaderName(file.direction, file.creator, contactQuery.data)}
										</DataGridRowItem>
										<DataGridRowItem>{formatFileSize(file.fileAttachment.fileSize)}</DataGridRowItem>
									</DataGridRow>
								);
							})}
						</DataGridRows>
					</DataGrid>
				)}
				{hasNextPage && !isFetchingNextPage && <Waypoint bottomOffset='-200px' onEnter={() => fetchNextPage()} />}
				{isFetchingNextPage && <LoadingSpinner type='extra-tiny' />}
			</div>
		</div>
	);
};
